/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-14
 */

// Add third-party dependencies.
import { useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { Provider, useSelector } from 'react-redux';

// Add local dependencies.
import { store } from '../../store';
import type { RootState } from '../../store';
import { clearUserStorage, markSignedOut } from '../../features/auth';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { StudioProvider } from '../../contexts/StudioContext';
import { SubscriptionProvider } from '../../contexts/SubscriptionContext';
import { StudioContextErrorBoundary } from '../common/StudioContextBoundary';
import AppRoutes from '../../routes/AppRoutes';
import SuccessAlertContainer from '../common/SuccessAlertContainer';
import { BottomTabBar } from '../navigation/mobile/BottomTabBar';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { ChatButton } from '../chatbot/ChatButton';
import { ChatWidget } from '../chatbot/ChatWidget';

/**
 * Inner app component that uses keyboard shortcuts within Router context.
 */
const AppWithKeyboardShortcuts: React.FC = () => {
  // Initialize keyboard shortcuts (must be inside Router context)
  useKeyboardShortcuts();

  return (
    <div data-testid="app">
      <AppRoutes />
      <SuccessAlertContainer
        filterByType={[
          'registration',
          'login',
          'logout',
          'profile',
          'password',
          'verification',
        ]}
      />
      <BottomTabBar />
      <ChatButton />
      <ChatWidget />
    </div>
  );
};

/**
 * Wipe the signed-out user's stored data and load the app fresh, so none of
 * their cached data (studios, students, chat, ...) is left for the next person.
 * The tab is then marked so its next sign-in skips the return path.
 */
const reloadSignedOut = () => {
  clearUserStorage();
  markSignedOut(); // after the wipe, which clears sessionStorage
  window.location.replace('/');
};

/**
 * App content component that conditionally wraps SubscriptionProvider.
 * Only provides subscription context for authenticated users.
 */
const AppContent: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const wasSignedIn = useRef(false);

  // Every sign-out path (Sign Out, session expiry, a 401) clears the user
  useEffect(() => {
    if (user) {
      wasSignedIn.current = true;
    } else if (wasSignedIn.current) {
      reloadSignedOut();
    }
  }, [user]);

  useEffect(() => {
    // Another tab signed out: this tab's session is gone too
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === 'tools-dance-auth-state' &&
        event.newValue === null &&
        wasSignedIn.current
      ) {
        reloadSignedOut();
      }
    };
    // Back/forward restored a frozen page that may predate a sign-out
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  return (
    <StudioContextErrorBoundary>
      <StudioProvider>
        {user ? (
          <SubscriptionProvider>
            <AppWithKeyboardShortcuts />
          </SubscriptionProvider>
        ) : (
          <AppWithKeyboardShortcuts />
        )}
      </StudioProvider>
    </StudioContextErrorBoundary>
  );
};

/**
 * Main application component.
 * @example
 * return (
 *   <App />
 * )
 * @returns {React.ReactElement} component.
 */
const App: React.FC = () => (
  // Render application with providers.
  <Provider store={store}>
    <ThemeProvider>
      <CssBaseline />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  </Provider>
);

// Export component.
export default App;
